import { useForm } from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import { TabContext, TabList } from '@mui/lab';
import { format } from 'date-fns';
import { MobileTimePicker } from '@mui/x-date-pickers';
import { AccessTime } from '@mui/icons-material';
import { Box, Button, ButtonGroup, Tab, Typography } from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';

import { INames } from 'apis/main/type';
import {
  getNandaDomain,
  createNursingRecord,
  updateNursingRecord,
} from 'apis/main';
import useUser from 'store/user/useUser';
import useStudent from 'store/student/useStudent';
import usePatient from 'store/patient/usePatient';
import MuiTextField from 'components/Form/MuiTextField';
import { requiredSelect } from 'components/Form/requiredItems';
import useNotification from 'hooks/useNotification';
import { findKeyValue } from 'utils/convert';

import { RECORD_TYPE } from '../Survey/type';
import { initialNursingRecord } from '../Survey/initialStates';

import Nanda from './Nanda';
import Soapie from './Soapie';
import Remarks from './Remarks';
import FocusDar from './FocusDar';
import NarrativeRecord from './NarrativeRecord';
import { StyledTabPanel } from '../style';

interface Props {
  coachRef: any;
}
const NANDA_VERSION_KEY = 'NANDA_VERSION';
const DEFAULT_NANDA_VERSION = 13;

const NursingRecords = ({ coachRef }: Props) => {
  const { isStudent } = useUser();
  const { student_uuid: user_id } = useStudent();
  const navigate = useNavigate();
  const { pathname } = useLocation();

  const { nursingRecord, patientInfo, onClearNursingRecord } = usePatient();
  const { onSuccess, onFail, onRequired } = useNotification();
  const { register, watch, setValue, handleSubmit, reset, getValues } =
    useForm();
  const [recordType, setRecordType] = useState<string>(RECORD_TYPE.NANDA);
  const [recordTime, setRecordTime] = useState<Date | null>(new Date());
  const [domainNames, setDomainNames] = useState<INames[]>([]);
  const [isTestMode, setIsTestMode] = useState<boolean>(false);

  const [nandaVersion, setNandaVersion] = useState<number>(() => {
    const saved = sessionStorage.getItem(NANDA_VERSION_KEY);
    const parsed = saved ? Number(saved) : DEFAULT_NANDA_VERSION;
    return Number.isFinite(parsed) ? parsed : DEFAULT_NANDA_VERSION;
  });

  useEffect(() => {
    sessionStorage.setItem(NANDA_VERSION_KEY, String(nandaVersion));
  }, [nandaVersion]);

  useEffect(() => {
    getNandaDomain({ version: nandaVersion }).then(({ data }) =>
      setDomainNames(data.names)
    );
  }, [nandaVersion]);

  const onRestNursingRecord = useCallback(() => {
    setRecordTime(new Date());

    // 난다부분
    setValue('collectingData', '');

    // 서술기록부분
    setValue('narrativeNote', '');

    // SOAPIE 부분
    setValue('subjective', '');
    setValue('objective', '');
    setValue('planning', '');

    // Focus DAR
    setValue('focus', '');
    setValue('data', '');
    setValue('action', '');
    setValue('response', '');

    reset(initialNursingRecord.nanda);
    reset(initialNursingRecord.soapie);
    reset(initialNursingRecord.remarks);
    onClearNursingRecord();
  }, [reset, onClearNursingRecord]);

  useEffect(() => {
    if (!nursingRecord) return onRestNursingRecord();

    const { record_time, record_type, content } = nursingRecord;

    const parsed = JSON.parse(content);
    reset(parsed);

    setRecordType(`${record_type}`);
    setRecordTime(new Date(record_time));

    if (`${record_type}` === RECORD_TYPE.NANDA && parsed?.version) {
      setNandaVersion(Number(parsed.version));
    }
  }, [nursingRecord, reset, onRestNursingRecord]);

  const onSubmit = (data: any) => {
    // 간호기록 시간 입력 체크
    if (!recordTime) return onRequired('REQUIRED.RECORD.TIME');

    let contentKeys: string[] = [];

    // update content
    switch (recordType) {
      case RECORD_TYPE.NANDA: {
        contentKeys = Object.keys(initialNursingRecord.nanda);
        break;
      }
      case RECORD_TYPE.SOAPIE: {
        contentKeys = Object.keys(initialNursingRecord.soapie);
        break;
      }
      case RECORD_TYPE.FOCUS_DAR: {
        contentKeys = Object.keys(initialNursingRecord.focusDar);
        break;
      }
      case RECORD_TYPE.NARRATIVE_RECORD: {
        contentKeys = Object.keys(initialNursingRecord.narrativeRecord);
        break;
      }
      case RECORD_TYPE.REMARKS: {
        contentKeys = Object.keys(initialNursingRecord.remarks);
        break;
      }
    }

    const content = findKeyValue(data, contentKeys);

    // nanda 필수값 체크
    if (recordType === RECORD_TYPE.NANDA) {
      if (
        !requiredSelect(content?.domain) ||
        !requiredSelect(content?.class) ||
        !requiredSelect(content?.diagnosis)
      ) {
        return onRequired('REQUIRED.NONCHOICE');
      }
    }

    const request = {
      recordType: Number(recordType),
      recordTime: format(recordTime, 'yyyy-MM-dd HH:mm'),
      content: JSON.stringify(content),
    };

    // 간호기록 수정
    if (nursingRecord) {
      return updateNursingRecord({ ...nursingRecord, ...request })
        .then(() => {
          onRestNursingRecord();
          navigate(`${pathname}?isUpdateNursingRecord=true`);
          onSuccess('Амжилттай хадгалагдлаа');
        })
        .catch(e => onFail('Хадгалж чадсангүй', e));
    }

    // 간호기록 생성
    createNursingRecord({
      userId: user_id,
      patientId: patientInfo!.patient_id,
      ...request,
    })
      .then(() => {
        onRestNursingRecord();
        navigate(`${pathname}?isUpdateNursingRecord=true`);
        onSuccess('Амжилттай хадгалагдлаа');
      })
      .catch(e => onFail('Хадгалж чадсангүй', e));
  };

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Box
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mt: 2.5, mb: 1.5 }}
      >
        <Typography variant="subtitle2" fontSize={13}>
          Сувилахуйн тэмдэглэл үүсгэх
        </Typography>
        <MobileTimePicker
          value={recordTime}
          onChange={setRecordTime}
          disabled={!isStudent}
          renderInput={params => (
            <MuiTextField
              placeholder="00:00"
              {...params}
              fullWidth={false}
              sx={{ width: 130 }}
              InputProps={{ endAdornment: <AccessTime /> }}
            />
          )}
        />
      </Box>
      <TabContext value={recordType}>
        <TabList
          variant="fullWidth"
          onChange={(_, value) => setRecordType(value)}
          ref={coachRef}
        >
          <Tab label="NANDA" value={RECORD_TYPE.NANDA} />
          <Tab label="SOAPIE" value={RECORD_TYPE.SOAPIE} />
          <Tab label="Focus DAR" value={RECORD_TYPE.FOCUS_DAR} />
          <Tab label="Тайлбар" value={RECORD_TYPE.NARRATIVE_RECORD} />
          <Tab label="Сувилахуйн үйл явц" value={RECORD_TYPE.REMARKS} />
        </TabList>
        <StyledTabPanel value={RECORD_TYPE.NANDA}>
          <Nanda
            watch={watch}
            setValue={setValue}
            register={register}
            disabled={!isStudent}
            domainNames={domainNames}
            isTestMode={isTestMode}
            nandaVersion={nandaVersion}
            setNandaVersion={setNandaVersion}
          />
        </StyledTabPanel>
        <StyledTabPanel value={RECORD_TYPE.SOAPIE}>
          <Soapie
            register={register}
            disabled={!isStudent}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>
        <StyledTabPanel value={RECORD_TYPE.FOCUS_DAR}>
          <FocusDar
            register={register}
            disabled={!isStudent}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>
        <StyledTabPanel value={RECORD_TYPE.NARRATIVE_RECORD}>
          <NarrativeRecord
            register={register}
            disabled={!isStudent}
            watch={watch}
            setValue={setValue}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>
        <StyledTabPanel value={RECORD_TYPE.REMARKS}>
          <Remarks
            register={register}
            disabled={!isStudent}
            isTestMode={isTestMode}
          />
        </StyledTabPanel>

        <ButtonGroup
          size="small"
          color="info"
          sx={{
            display: 'flex',
            justifyContent: 'flex-end',
            paddingBottom: '120px',
          }}
        >
          <Button
            variant="text"
            color="inherit"
            disabled={!isStudent}
            onClick={onRestNursingRecord}
          >
            Цуцлах
          </Button>
          <Button
            style={{ color: 'purple' }}
            variant="text"
            type="submit"
            disabled={!patientInfo || !isStudent}
          >
            Хадгалах / Гарын үсэг
          </Button>
        </ButtonGroup>
      </TabContext>
    </form>
  );
};

export default NursingRecords;
